import { useEffect, useRef, useState } from 'react';
import { Box, Paper, Button, IconButton } from '@mui/material';
import '../Common/typography.css';
import {
  Criteria,
  SavedScreener,
  CreateScreenerResult,
} from '../../types/screener.type';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter.svg';
import { ReactComponent as SaveIcon } from '../../assets/icons/save-line.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh.svg';
import { MarketCriteriaItem } from '../Screener/Criteria/MarketCriteria/MarketCriteria';
import { SectorCriteriaItem } from '../Screener/Criteria/SectorCriteria/SectorCriteria';
import SectorCriteria from '../Screener/Criteria/SectorCriteria/SectorCriteria';
import '../Screener/Criteria/AdvancedCriteriaPopup/AdvancedCriteriaPopup.css';
import ScreenerDropdown from './ScreenerDropdown/ScreenerDropdown';
import ScreenerSaveDialog from './ScreenerSaveDialog/ScreenerSaveDialog';
import './ScreenerSaveDialog/ScreenerSaveDialog.css';
import { ThemeType } from '../../utils/type';
import './ScreenerMenu.css';
interface ScreenerMenuProps {
  themeColor: string;
  isSmallScreen: boolean;
  savedScreeners: SavedScreener[];
  currentScreenerId: string | null;
  currentPresetId: string | null;
  screenerName: string;
  criteria: Criteria[];
  markets: MarketCriteriaItem[];
  sectors: SectorCriteriaItem[];
  setCurrentScreenerId: (currentScreenerId: string) => void;
  onSelectPreset: (presetId: string) => void;
  setSectors: (sectors: SectorCriteriaItem[]) => void;
  setOpenAdvancedCriteria: (openAdvancedCriteria: boolean) => void;
  setOpenSPAdvancedCriteria: (openSPAdvancedCriteria: boolean) => void;
  onRefresh: () => void;
  onResetAll: () => void;
  onCreateScreener: (
    name: string,
    criteria: Criteria[],
    markets: MarketCriteriaItem[],
    sectors: SectorCriteriaItem[],
  ) => CreateScreenerResult;
  onUpdateScreener: (
    id: string,
    name: string,
    criteria: Criteria[],
    markets: MarketCriteriaItem[],
    sectors: SectorCriteriaItem[],
  ) => boolean;
  onDeleteScreener: (id: string) => void;
  setSkipApiCall: (skipApiCall: boolean) => void;
}

const commonButtonStyle = {
  boxSizing: 'border-box',
  backgroundColor: 'var(--surface-primary)',
  border: `1px solid var(--color-brand)`,
  borderRadius: '4px',
  strokeWidth: '1px',
  color: 'black',
  textTransform: 'none',
  fontWeight: 'normal',
  '& svg': {
    width: '20px',
    height: '20px',
  },
};

const ScreenerMenu = ({
  themeColor,
  isSmallScreen,
  setOpenAdvancedCriteria,
  setOpenSPAdvancedCriteria,
  savedScreeners,
  currentScreenerId,
  currentPresetId,
  setCurrentScreenerId,
  onSelectPreset,
  onRefresh,
  onResetAll,
  screenerName,
  criteria,
  markets,
  sectors,
  setSectors,
  onCreateScreener,
  onUpdateScreener,
  onDeleteScreener,
  setSkipApiCall,
}: ScreenerMenuProps) => {
  const [openSaveDialog, setOpenSaveDialog] = useState(false);
  const saveDialogRef = useRef<HTMLDivElement>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const deleteConfirmRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // If the click is outside the dropdown component, close it
      if (
        saveDialogRef.current &&
        !saveDialogRef.current.contains(event.target as Node)
      ) {
        setOpenSaveDialog(false);
      }
      if (
        deleteConfirmRef.current &&
        !deleteConfirmRef.current.contains(event.target as Node)
      ) {
        setDeleteConfirmId(null);
      }
    };

    // Add event listener for clicks
    document.addEventListener('mousedown', handleClickOutside);
    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []); // Empty dependency array means this effect runs once on mount and cleans up on unmount

  const deleteTarget = savedScreeners.find((s) => s.id === deleteConfirmId);

  const deleteConfirmPopup = deleteTarget && (
    <Box
      ref={deleteConfirmRef}
      className={`screener-save-dialog${
        isSmallScreen ? ' screener-save-dialog--mobile-overlay' : ''
      }`}
    >
      <Box className="screener-save-dialog-header">
        <p className="screener-save-dialog-title">Remove strategy?</p>
      </Box>
      <Box className="screener-save-dialog-confirm">
        <p className="screener-save-dialog-confirm-text body-m-regular">
          {`Are you sure you want to remove "${deleteTarget.name}"?`}
        </p>
      </Box>
      <Box className="screener-save-dialog-actions">
        <Button
          className="screener-save-dialog-compact-button"
          onClick={() => setDeleteConfirmId(null)}
        >
          Cancel
        </Button>
        <Button
          className="screener-save-dialog-compact-button screener-save-dialog-compact-button--primary"
          onClick={() => {
            onDeleteScreener(deleteTarget.id);
            setDeleteConfirmId(null);
          }}
        >
          Yes
        </Button>
      </Box>
    </Box>
  );

  const screenerCriteriaButton = (
    <Button
      sx={{
        ...commonButtonStyle,
        flex: 1,
        minWidth: 0,
        height: '40px',
        padding: '8px 12px',
        display: 'flex',
        borderColor: 'var(--input-border)',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '10px',
        '& .MuiButton-startIcon': {
          marginRight: '0px',
          marginLeft: '0px',
        },
        '& svg': {
          width: '16px',
          height: '16px',
        },
        '&:hover': {
          border: '1px solid var(--border-primary-brand)',
        },
      }}
      startIcon={<FilterIcon className="filter-icon" />}
      onClick={() =>
        isSmallScreen
          ? setOpenSPAdvancedCriteria(true)
          : setOpenAdvancedCriteria(true)
      }
    >
      <span
        className="body-m-regular"
        style={{ color: 'var(--text-primary)', whiteSpace: 'nowrap' }}
      >
        {`Screener Criteria (${criteria.filter((item: Criteria) => item.selected).length})`}
      </span>
    </Button>
  );

  const saveIconButton = (
    <Box ref={saveDialogRef} sx={{ flexShrink: 0 }}>
      <IconButton
        sx={{
          ...commonButtonStyle,
          width: '40px',
          height: '40px',
          backgroundColor: openSaveDialog
            ? themeColor
            : 'var(--surface-primary)',
          '&:hover': {
            backgroundColor: openSaveDialog
              ? themeColor
              : 'var(--color-brand-hover2)',
          },
          '& svg path': {
            fill: openSaveDialog ? 'var(--text-invert)' : themeColor,
          },
        }}
        onClick={() => setOpenSaveDialog(!openSaveDialog)}
      >
        <SaveIcon />
      </IconButton>
      {openSaveDialog && (
        <ScreenerSaveDialog
          savedScreeners={savedScreeners}
          setOpenSaveDialog={setOpenSaveDialog}
          screenerName={screenerName}
          currentScreenerId={currentScreenerId}
          currentPresetId={currentPresetId}
          setCurrentScreenerId={setCurrentScreenerId}
          criteria={criteria}
          markets={markets}
          sectors={sectors}
          onCreateScreener={onCreateScreener}
          onUpdateScreener={onUpdateScreener}
          setSkipApiCall={setSkipApiCall}
        />
      )}
    </Box>
  );

  const refreshIconButton = (
    <IconButton
      sx={{
        ...commonButtonStyle,
        flexShrink: 0,
        width: '40px',
        height: '40px',
        '& svg path': {
          fill: themeColor,
        },
        '&:hover': {
          backgroundColor: 'var(--color-brand-hover2)',
        },
      }}
      onClick={onRefresh}
    >
      <RefreshIcon />
    </IconButton>
  );

  if (isSmallScreen) {
    return (
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          gap: '8px',
          padding: '0px 16px',
        }}
      >
        {/* Row 1: Saved Strategy Dropdown */}
        <Box sx={{ width: '100%' }}>
          <ScreenerDropdown
            screenerName={screenerName}
            setCurrentScreenerId={setCurrentScreenerId}
            currentPresetId={currentPresetId}
            onSelectPreset={onSelectPreset}
            isSmallScreen={isSmallScreen}
            selectedScreenerId={currentScreenerId || null}
            screeners={savedScreeners}
            onRequestDelete={setDeleteConfirmId}
          />
        </Box>

        {/* Row 2: Screener Criteria Button + Save + Refresh */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {screenerCriteriaButton}
          {saveIconButton}
          {refreshIconButton}
        </Box>
        {deleteConfirmPopup}
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        position: 'relative',
        gap: '8px',
        padding: '0px 32px',
        alignItems: 'center',
      }}
    >
      {/* Saved Strategy Dropdown */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ScreenerDropdown
          screenerName={screenerName}
          setCurrentScreenerId={setCurrentScreenerId}
          currentPresetId={currentPresetId}
          onSelectPreset={onSelectPreset}
          isSmallScreen={isSmallScreen}
          selectedScreenerId={currentScreenerId || null}
          screeners={savedScreeners}
          onRequestDelete={setDeleteConfirmId}
        />
      </Box>

      {/* Sector Dropdown */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <SectorCriteria
          sectors={sectors}
          setSectors={setSectors}
          isHeaderStyle
        />
      </Box>

      {screenerCriteriaButton}

      {/* Reset All Button */}
      <Button
        sx={{
          ...commonButtonStyle,
          flexShrink: 0,
          height: '40px',
          padding: '8px 12px',
          borderColor: 'var(--color-brand)',
          '&:hover': {
            backgroundColor: 'var(--color-brand-hover2)',
          },
        }}
        onClick={onResetAll}
      >
        <span
          className="body-m-bold"
          style={{ color: 'var(--color-brand)', whiteSpace: 'nowrap' }}
        >
          Reset all
        </span>
      </Button>

      {saveIconButton}
      {refreshIconButton}
      {deleteConfirmPopup}
    </Paper>
  );
};

export default ScreenerMenu;
